import type { AskUserAnswer, AskUserQuestion } from '../types/inbox';

/**
 * Render a set of `ask_user` answers into the human-readable text shown in the
 * conversation transcript when the answer is sent over the live runtime socket
 * (it becomes the visible user bubble, while the structured `answers` ride
 * alongside on the prompt frame). This string is for display only — the backend
 * resolves the request from the structured `answers`, never by parsing this.
 *
 * - Single question: just the answer value (the common free-text case), so the
 *   transcript reads like a natural reply rather than a Q/A dump.
 * - Multiple questions: one `Question — Answer` line each, with any comment
 *   appended in parentheses.
 */
export function formatAskUserAnswers(
  questions: AskUserQuestion[],
  answers: AskUserAnswer[],
): string {
  const byId = new Map(answers.map((a) => [a.questionId, a]));

  const renderValue = (value: string | string[] | undefined): string =>
    Array.isArray(value) ? value.filter(Boolean).join(', ') : (value ?? '');

  const renderComment = (comment?: string): string =>
    comment?.trim() ? ` (${comment.trim()})` : '';

  if (questions.length === 1) {
    const a = byId.get(questions[0].questionId);
    return `${renderValue(a?.value)}${renderComment(a?.comment)}`.trim();
  }

  return questions
    .map((q) => {
      const a = byId.get(q.questionId);
      const label = q.header?.trim() || q.question.trim();
      return `${label} — ${renderValue(a?.value)}${renderComment(a?.comment)}`;
    })
    .join('\n');
}
